import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { WhenLoaded } from '@openx/components-core/lib/Loader/WhenLoaded.tsx';
import { RHFSwitchField } from '@openx/components-core/lib/SwitchField/RHFSwitchField.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { type DealFormData, dealFormField } from '@openx/types/deal.ts';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { DealDiscount } from '../../components/Discounts/DealDiscount.tsx';
import { useDealContext } from '../../utils/context/DealContext.tsx';

export const DiscountsFormSection = () => {
  const { useDealDiscounts, useDemandPartnersFetch, useOnBehalfOfAccountId } = useDealContext();
  const { t } = useTranslation();
  const onBehalfOfAccountId = useOnBehalfOfAccountId();

  const observableIsDiscountEnabled = useWatch<DealFormData, typeof dealFormField.IS_DISCOUNT_ENABLED>({
    name: dealFormField.IS_DISCOUNT_ENABLED,
  });

  const observableParticipants = useWatch<DealFormData, typeof dealFormField.DEAL_PARTICIPANTS>({
    name: dealFormField.DEAL_PARTICIPANTS,
  });

  const participants = useMemo(() => {
    return observableParticipants.map(({ buyer_id, buyer_ids, demand_partner, main_buyer_id }) => ({
      demandPartnerId: demand_partner ?? '',
      receivedSeatIds: [buyer_id, ...(buyer_ids ?? []), main_buyer_id].filter(Boolean) as string[],
    }));
  }, [observableParticipants]);

  const { data, isLoading } = useDealDiscounts(participants);
  const { loading: isLoadingDemandPartnerOptions, mapById: demandPartnerById } = useDemandPartnersFetch({
    account_uid: onBehalfOfAccountId,
  });

  const isAnythingLoading = isLoading || isLoadingDemandPartnerOptions;
  const hasAnyDiscountOrIsLoading = isAnythingLoading || data.length !== 0;

  return (
    <FormSection divider rightColumnSize={RIGHT_COLUMN_WIDTH + 1} title={t('Discounts')}>
      <RHFSwitchField
        data-test="discounts-section-toggle"
        label={t('Enable Discounts for this Deal')}
        name={dealFormField.IS_DISCOUNT_ENABLED}
      />

      {observableIsDiscountEnabled && (
        <IndentedSections>
          <Alert severity="info">
            {t('Your buyer discounts will be applied to this deal. You can manage them')}{' '}
            <Link target="_blank" to="/discounts">
              {t('here')}
            </Link>
            . {t('You can also disable all of them for this deal.')}
          </Alert>

          <Stack
            spacing={1.5}
            data-test="discounts-stack"
            sx={{
              mt: hasAnyDiscountOrIsLoading ? 1.5 : 0,
            }}
          >
            <WhenLoaded loading={isAnythingLoading} noPadding>
              {data.map(datum => (
                <DealDiscount
                  bannerAndVideoDiscount={datum.banner_and_video_discount}
                  ctvDiscount={datum.ctv_discount}
                  demandPartnerById={demandPartnerById}
                  demandPartnerId={datum.demand_partner_id}
                  key={datum.id}
                  name={datum.name}
                  seatIds={datum.seat_ids}
                />
              ))}
            </WhenLoaded>
          </Stack>
        </IndentedSections>
      )}
    </FormSection>
  );
};
